import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { format, parseISO } from "date-fns";

/**
 * Merge Tailwind classes without conflicts.
 * Combines clsx (conditional classes) with tailwind-merge (dedup utilities).
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format an ISO date string for display.
 * @param dateStr  ISO-8601 date string
 * @param pattern  date-fns format pattern (default: "MMM d, yyyy")
 */
export function formatDate(dateStr: string, pattern = "MMM d, yyyy"): string {
  try {
    return format(parseISO(dateStr), pattern);
  } catch {
    return dateStr;
  }
}

/** Platform currency: Ghana cedis unless the build sets VITE_CURRENCY (must match the API's PLATFORM_CURRENCY). */
export const APP_CURRENCY = (import.meta.env.VITE_CURRENCY as string | undefined) || "GHS";
const APP_LOCALE = (import.meta.env.VITE_LOCALE as string | undefined) || "en-GH";

/** Format an amount in the platform currency, e.g. 1234.5 → "GH₵1,234.50". */
export function formatCurrency(amount: number, opts: { decimals?: number } = {}): string {
  return new Intl.NumberFormat(APP_LOCALE, {
    style: "currency",
    currency: APP_CURRENCY,
    ...(opts.decimals != null ? { minimumFractionDigits: opts.decimals, maximumFractionDigits: opts.decimals } : {}),
  }).format(Number.isFinite(amount) ? amount : 0);
}

/** Format minor units (pesewas / cents); "—" for missing values. */
export function formatMinor(cents: number | null | undefined, opts: { decimals?: number } = {}): string {
  if (cents == null || !Number.isFinite(cents)) return "—";
  return formatCurrency(cents / 100, opts);
}

/** The currency symbol on its own, for input prefixes ("GH₵"). */
export function currencySymbol(): string {
  return (
    new Intl.NumberFormat(APP_LOCALE, { style: "currency", currency: APP_CURRENCY })
      .formatToParts(0)
      .find((p) => p.type === "currency")?.value ?? APP_CURRENCY
  );
}

/**
 * Format bytes to a human-readable string.
 * @example formatBytes(1258291) => "1.2 MB"
 * @example formatBytes(348160)  => "340 KB"
 */
export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return "0 Bytes";

  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`;
}

/**
 * Format an ISO date string with full date + time.
 * @example formatDateTime("2026-02-21T14:30:00Z") => "Feb 21, 2026 14:30:00"
 */
export function formatDateTime(dateStr: string): string {
  try {
    return format(parseISO(dateStr), "MMM d, yyyy HH:mm:ss");
  } catch {
    return dateStr;
  }
}

/**
 * Truncate a string, appending "…" if it exceeds the given length.
 * @example truncate("Hello World", 5) => "Hello…"
 */
export function truncate(str: string, length: number): string {
  return str.length <= length ? str : `${str.slice(0, length)}…`;
}

/**
 * Generate initials from a full name.
 */
export function getInitials(name: string): string {
  return name
    .split(" ")
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}
